import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// Use environment variable for Mapbox access token
mapboxgl.accessToken =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  "pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw";

const MapComponent = ({ height = "400px", addresses = [] }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [showResetButton, setShowResetButton] = useState(false);
  const [originalBounds, setOriginalBounds] = useState(null);

  // Enhanced geocoding function
  const geocodeAddresses = async (addresses) => {
    const geocodedLocations = [];

    for (const address of addresses) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${mapboxgl.accessToken}`,
        );

        if (!response.ok) {
          if (response.status === 403) {
            console.error(
              "Mapbox API access denied. Please check your API token.",
            );
            return [];
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data && data.features && data.features.length > 0) {
          const feature = data.features[0];
          geocodedLocations.push({
            lng: feature.center[0],
            lat: feature.center[1],
            name: address,
            fullName: feature.place_name,
          });
        }
      } catch (error) {
        console.error("Geocoding error for address:", address, error);
      }
    }

    return geocodedLocations;
  };

  // Create custom marker element with animations
  const createCustomMarker = (location, index) => {
    const markerElement = document.createElement("div");
    markerElement.className = "custom-marker";
    markerElement.innerHTML = `
      <div class="marker-pulse"></div>
      <div class="marker-inner">
        <div class="marker-dot"></div>
        <div class="marker-label">${index + 1}</div>
      </div>
    `;

    // Add click event for marker animation
    markerElement.addEventListener("click", () => {
      setActiveMarker(index);
      markerElement.classList.add("marker-clicked");
      setTimeout(() => {
        markerElement.classList.remove("marker-clicked");
      }, 600);
    });

    return markerElement;
  };

  // Reset map to show all locations
  const resetMapView = () => {
    if (mapInstanceRef.current && originalBounds) {
      mapInstanceRef.current.fitBounds(originalBounds, {
        padding: 80,
        duration: 1500,
        easing: (t) => t * (2 - t),
      });
      setShowResetButton(false);
      setActiveMarker(null);
    }
  };

  // Initialize map with enhanced features
  useEffect(() => {
    const initializeMap = async () => {
      try {
        setIsLoading(true);

        // Check if Mapbox token is available
        if (!mapboxgl.accessToken || mapboxgl.accessToken.includes("demo")) {
          console.error(
            "Please set REACT_APP_MAPBOX_TOKEN environment variable with a valid Mapbox token",
          );
          if (mapRef.current) {
            mapRef.current.innerHTML = `
              <div style="padding: 40px; text-align: center; color: #666; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 15px; color: white;">
                <div style="font-size: 24px; margin-bottom: 10px;">🗺️</div>
                <div style="font-size: 18px; margin-bottom: 10px;">Map Service Unavailable</div>
                <div style="font-size: 14px; opacity: 0.8;">Please configure Mapbox API token</div>
              </div>
            `;
          }
          setIsLoading(false);
          return;
        }

        // Wait for DOM to be ready
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (!mapRef.current) return;

        // Check container dimensions
        const rect = mapRef.current.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
          setTimeout(initializeMap, 300);
          return;
        }

        // Geocode addresses
        let geocodedLocations = [];
        if (addresses && addresses.length > 0) {
          console.log("Input addresses:", addresses);
          geocodedLocations = await geocodeAddresses(addresses);
          console.log(
            "Geocoding completed:",
            geocodedLocations.length,
            "locations found",
          );
        }

        // Start with world view for animation
        let center = [0, 20]; // World center
        let zoom = 2; // World zoom level
        let targetCenter = [-123.1207, 49.2827]; // Default target
        let targetZoom = 10; // Default target zoom

        // Calculate bounds for locations
        if (geocodedLocations.length > 0) {
          if (geocodedLocations.length === 1) {
            targetCenter = [geocodedLocations[0].lng, geocodedLocations[0].lat];
            targetZoom = 14;
          } else {
            const lngs = geocodedLocations.map((loc) => loc.lng);
            const lats = geocodedLocations.map((loc) => loc.lat);
            targetCenter = [
              (Math.min(...lngs) + Math.max(...lngs)) / 2,
              (Math.min(...lats) + Math.max(...lats)) / 2,
            ];
            targetZoom = 11;
          }
        }

        // Store geocoded locations for click events
        const campusLocations = geocodedLocations;

        // Initialize map with enhanced style
        const map = new mapboxgl.Map({
          container: mapRef.current,
          style: "mapbox://styles/mapbox/light-v11",
          center: center,
          zoom: zoom,
          pitch: 45,
          bearing: 0,
          scrollZoom: false, // Disable scroll zoom to prevent zooming with cursor movement
          doubleClickZoom: true,
          dragPan: true,
          keyboard: true,
          touchZoomRotate: true,
          antialias: true,
          fadeDuration: 300,
        });

        mapInstanceRef.current = map;

        // Add enhanced navigation controls
        const nav = new mapboxgl.NavigationControl({
          visualizePitch: true,
          showZoom: true,
          showCompass: true,
        });
        map.addControl(nav, "top-right");

        // Add fullscreen control
        map.addControl(new mapboxgl.FullscreenControl(), "top-right");

        // Map load event
        map.on("load", () => {
          // Check if map is still mounted
          if (!mapInstanceRef.current) return;

          setIsMapReady(true);
          setIsLoading(false);

          // Add custom styling layers
          map.addSource("mapbox-dem", {
            type: "raster-dem",
            url: "mapbox://mapbox.mapbox-terrain-dem-v1",
            tileSize: 512,
            maxzoom: 14,
          });

          // Debug: Log geocoded locations
          console.log("Geocoded locations:", geocodedLocations);

          // Ensure we have locations to display
          if (geocodedLocations.length === 0) {
            console.warn(
              "No geocoded locations found! Check if addresses are being processed correctly.",
            );
            return;
          }

          // Add markers with animations after map animation completes
          geocodedLocations.forEach((location, index) => {
            if (location.lat && location.lng) {
              console.log(`Creating marker ${index + 1}:`, location);

              // Create custom marker
              const markerElement = createCustomMarker(location, index);

              // Enhanced popup with detailed coordinate information
              const popup = new mapboxgl.Popup({
                offset: {
                  bottom: [0, -15],
                  top: [0, 15],
                  left: [15, 0],
                  right: [-15, 0],
                },
                closeButton: true,
                closeOnClick: false,
                className: "custom-popup enhanced-popup",
                maxWidth: "350px",
              }).setHTML(`
                <div class="popup-content">
                  <div class="popup-header">
                    <h3>📍 ${location.name}</h3>
                  </div>
                  <div class="popup-body">
                    <div class="popup-address">
                      <strong>Address:</strong><br/>
                      <p>${location.fullName}</p>
                    </div>
                    <div class="popup-coordinates">
                      <strong>Coordinates:</strong><br/>
                      <div class="coordinate-grid">
                        <div class="coordinate-item">
                          <span class="coordinate-label">Latitude:</span>
                          <span class="coordinate-value">${location.lat.toFixed(6)}</span>
                        </div>
                        <div class="coordinate-item">
                          <span class="coordinate-label">Longitude:</span>
                          <span class="coordinate-value">${location.lng.toFixed(6)}</span>
                        </div>
                      </div>
                    </div>
                    <div class="popup-actions">
                      <button class="view-details-btn" onclick="window.open('https://maps.google.com/?q=${location.lat},${location.lng}', '_blank')">
                        🗺️ View in Google Maps
                      </button>
                    </div>
                  </div>
                </div>
              `);

              // Create marker with custom element
              const marker = new mapboxgl.Marker({
                element: markerElement,
                anchor: "bottom",
              })
                .setLngLat([location.lng, location.lat])
                .setPopup(popup)
                .addTo(map);

              // Fallback: Also create a simple default marker as backup
              const fallbackMarker = new mapboxgl.Marker({
                color: "#e74c3c",
              })
                .setLngLat([location.lng, location.lat])
                .setPopup(popup);

              // Show fallback marker if custom marker fails to appear
              setTimeout(() => {
                const isCustomMarkerVisible =
                  markerElement.style.opacity === "1" ||
                  markerElement.classList.contains("marker-animate-in");
                if (!isCustomMarkerVisible) {
                  console.log(
                    `Adding fallback marker for location ${index + 1}`,
                  );
                  fallbackMarker.addTo(map);
                }
              }, 2000);

              // Enhanced click event for coordinate details
              markerElement.addEventListener("click", (e) => {
                e.stopPropagation();

                // Show popup first so Mapbox can auto-pan
                popup.addTo(map);

                // Smooth zoom/pan with upward offset so the popup has breathing room below
                map.easeTo({
                  center: [location.lng, location.lat],
                  zoom: 16,
                  offset: [0, -150], // shift map up by 150px
                  duration: 1500,
                  easing: (t) => t * (2 - t),
                });

                // Add bounce animation to clicked marker
                markerElement.classList.add("marker-clicked");
                setTimeout(() => {
                  markerElement.classList.remove("marker-clicked");
                }, 600);

                // Set active marker and show reset button
                setActiveMarker(index);
                setShowResetButton(true);
              });

              // Show markers immediately for debugging
              console.log(`Making marker ${index + 1} visible`);

              // Add marker to map first
              markersRef.current.push(marker);

              // Then make it visible with multiple approaches
              setTimeout(() => {
                markerElement.classList.add("marker-animate-in");

                // Force visibility as backup
                markerElement.style.opacity = "1";
                markerElement.style.transform = "translateY(0) scale(1)";
                markerElement.style.visibility = "visible";
                markerElement.style.display = "block";

                console.log(`Marker ${index + 1} should now be visible`);
              }, 100);

              // Alternative: Add entrance animation delay if needed
              // setTimeout(() => {
              //   markerElement.classList.add('marker-animate-in');
              // }, 500 + (index * 100));
            }
          });

          // Animate from world view to target location
          setTimeout(() => {
            if (
              mapInstanceRef.current &&
              mapInstanceRef.current.isStyleLoaded()
            ) {
              try {
                // First animate to target center and zoom
                mapInstanceRef.current.flyTo({
                  center: targetCenter,
                  zoom: targetZoom,
                  duration: 3000,
                  easing: (t) => t * (2 - t), // easeOutQuad
                });

                // If multiple locations, fit bounds after reaching target
                if (geocodedLocations.length > 1) {
                  setTimeout(() => {
                    const bounds = new mapboxgl.LngLatBounds();
                    geocodedLocations.forEach((loc) => {
                      bounds.extend([loc.lng, loc.lat]);
                    });

                    // Store original bounds for reset functionality
                    setOriginalBounds(bounds);

                    mapInstanceRef.current.fitBounds(bounds, {
                      padding: 80,
                      duration: 2000,
                      easing: (t) => t * (2 - t), // easeOutQuad
                    });
                  }, 3000);
                } else if (geocodedLocations.length === 1) {
                  // Store single location bounds for reset
                  const bounds = new mapboxgl.LngLatBounds();
                  const loc = geocodedLocations[0];
                  bounds.extend([loc.lng - 0.01, loc.lat - 0.01]);
                  bounds.extend([loc.lng + 0.01, loc.lat + 0.01]);
                  setOriginalBounds(bounds);
                }
              } catch (error) {
                console.error("Error animating map:", error);
              }
            }
          }, 1000);

          // Add hover effects
          map.on("mouseenter", () => {
            map.getCanvas().style.cursor = "grab";
          });

          map.on("mousedown", () => {
            map.getCanvas().style.cursor = "grabbing";
          });

          map.on("mouseup", () => {
            map.getCanvas().style.cursor = "grab";
          });

          // Add double-click to zoom to nearest campus
          map.on("dblclick", (e) => {
            const clickedPoint = e.lngLat;
            let nearestLocation = null;
            let minDistance = Infinity;

            // Find the nearest campus location
            campusLocations.forEach((location) => {
              const distance = Math.sqrt(
                Math.pow(clickedPoint.lng - location.lng, 2) +
                  Math.pow(clickedPoint.lat - location.lat, 2),
              );
              if (distance < minDistance) {
                minDistance = distance;
                nearestLocation = location;
              }
            });

            // Zoom to the nearest campus if within reasonable distance
            if (nearestLocation && minDistance < 0.1) {
              // Within ~10km
              map.flyTo({
                center: [nearestLocation.lng, nearestLocation.lat],
                zoom: 16,
                duration: 2000,
                easing: (t) => t * (2 - t),
              });
            }
          });

          // Resize handler with null check
          setTimeout(() => {
            if (
              mapInstanceRef.current &&
              mapInstanceRef.current.isStyleLoaded()
            ) {
              try {
                mapInstanceRef.current.resize();
              } catch (error) {
                console.error("Error resizing map:", error);
              }
            }
          }, 250);
        });

        // Error handling
        map.on("error", (e) => {
          console.error("Mapbox error:", e.error);
          setIsLoading(false);
        });

        // Style change animation
        map.on("styledata", () => {
          map.getCanvas().style.transition = "opacity 0.3s ease-in-out";
        });
      } catch (error) {
        console.error("Error initializing map:", error);
        setIsLoading(false);
        if (mapRef.current) {
          mapRef.current.innerHTML = `
            <div style="padding: 40px; text-align: center; color: #666; background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%); border-radius: 15px; color: white;">
              <div style="font-size: 24px; margin-bottom: 10px;">⚠️</div>
              <div style="font-size: 18px; margin-bottom: 10px;">Map Loading Error</div>
              <div style="font-size: 14px; opacity: 0.8;">Please try again later</div>
            </div>
          `;
        }
      }
    };

    const timeoutId = setTimeout(initializeMap, 300);

    return () => {
      clearTimeout(timeoutId);

      // Cleanup markers
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      // Cleanup map
      if (mapInstanceRef.current) {
        try {
          // Clear any pending operations
          mapInstanceRef.current.off();
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        } catch (error) {
          console.error("Error cleaning up map:", error);
        }
      }
    };
  }, [addresses]);

  return (
    <div
      className="map-wrapper"
      style={{
        position: "relative",
        height,
        width: "100%",
        "--map-height": height,
      }}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div className="map-loading-overlay">
          <div className="map-loading-content">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading Interactive Map...</div>
          </div>
        </div>
      )}

      {/* Map container */}
      <div
        ref={mapRef}
        className={`map-container ${isMapReady ? "map-ready" : ""}`}
        style={{
          height: "100%",
          width: "100%",
          borderRadius: "15px",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          border: "3px solid rgba(255,255,255,0.2)",
          position: "relative",
        }}
      />

      {/* Map controls overlay */}
      {isMapReady && (
        <div className="map-controls-overlay">
          <div className="map-style-switcher">
            <button
              onClick={() =>
                mapInstanceRef.current?.setStyle(
                  "mapbox://styles/mapbox/light-v11",
                )
              }
              className="style-btn"
            >
              Light
            </button>
            <button
              onClick={() =>
                mapInstanceRef.current?.setStyle(
                  "mapbox://styles/mapbox/dark-v11",
                )
              }
              className="style-btn"
            >
              Dark
            </button>
            <button
              onClick={() =>
                mapInstanceRef.current?.setStyle(
                  "mapbox://styles/mapbox/satellite-v9",
                )
              }
              className="style-btn"
            >
              Satellite
            </button>
          </div>
        </div>
      )}

      {/* Reset View Button */}
      {showResetButton && isMapReady && (
        <div className="map-reset-overlay">
          <button
            className="reset-view-btn"
            onClick={resetMapView}
            title="View All Locations"
          >
            🔄 View All Locations
          </button>
        </div>
      )}

      {/* Custom CSS styles */}
      <style jsx>{`
        .map-wrapper {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 15px;
          padding: 3px;
          transition: all 0.3s ease;
        }

        .map-wrapper:hover {
          transform: translateY(-2px);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
        }

        .map-container {
          opacity: 0;
          transform: scale(0.95);
          transition: all 0.5s ease;
        }

        .map-container.map-ready {
          opacity: 1;
          transform: scale(1);
        }

        .map-loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          border-radius: 15px;
        }

        .map-loading-content {
          text-align: center;
          color: white;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        .loading-text {
          font-size: 16px;
          font-weight: 600;
          opacity: 0.9;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .map-controls-overlay {
          position: absolute;
          top: 15px;
          left: 15px;
          z-index: 1001;
        }

        .map-reset-overlay {
          position: absolute;
          top: 15px;
          right: 15px;
          z-index: 1001;
        }

        .map-style-switcher {
          display: flex;
          gap: 5px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 8px;
          padding: 5px;
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .style-btn {
          padding: 8px 12px;
          border: none;
          border-radius: 5px;
          background: transparent;
          color: #333;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .style-btn:hover {
          background: #667eea;
          color: white;
          transform: translateY(-1px);
        }

        .reset-view-btn {
          background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 25px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(231, 76, 60, 0.3);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          gap: 8px;
          animation: slideInFromRight 0.3s ease-out;
        }

        .reset-view-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(231, 76, 60, 0.4);
          background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
        }

        .reset-view-btn:active {
          transform: translateY(0);
          box-shadow: 0 4px 15px rgba(231, 76, 60, 0.3);
        }

        @keyframes slideInFromRight {
          0% {
            opacity: 0;
            transform: translateX(30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .custom-marker {
          position: relative;
          width: 40px;
          height: 40px;
          cursor: pointer;
          opacity: 0;
          transform: translateY(20px) scale(0.5);
          transition: all 0.4s ease;
        }

        .custom-marker.marker-animate-in {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        .custom-marker.marker-clicked {
          animation: markerBounce 0.6s ease;
        }

        .marker-pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: rgba(255, 87, 34, 0.3);
          transform: translate(-50%, -50%);
          animation: pulse 2s infinite;
        }

        .marker-inner {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 30px;
          height: 30px;
          background: linear-gradient(135deg, #ff5722, #ff8a65);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(255, 87, 34, 0.4);
          border: 3px solid white;
        }

        .marker-dot {
          position: absolute;
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          animation: dotPulse 1.5s infinite;
        }

        .marker-label {
          color: white;
          font-size: 12px;
          font-weight: bold;
          z-index: 1;
        }

        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(2);
            opacity: 0;
          }
        }

        @keyframes dotPulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.7;
          }
        }

        @keyframes markerBounce {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-10px) scale(1.1);
          }
        }

        .mapboxgl-popup {
          z-index: 1002;
          max-width: none !important;
        }

        .mapboxgl-popup-content {
          border-radius: 16px;
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
          border: none;
          padding: 0;
          max-width: 350px;
          /* Ensure long content stays within the map container – uses CSS var set on wrapper */
          max-height: calc(var(--map-height) - 60px);
          overflow-y: auto;
          overflow-x: hidden;
          position: relative;
        }

        .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
          border-top-color: #f8f9fa;
        }

        .mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
          border-bottom-color: #f8f9fa;
        }

        .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
          border-right-color: #f8f9fa;
        }

        .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
          border-left-color: #f8f9fa;
        }

        .enhanced-popup .mapboxgl-popup-content {
          max-width: 400px;
        }

        .popup-content {
          padding: 20px;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        }

        .popup-header h3 {
          margin: 0 0 15px 0;
          color: #2c3e50;
          font-size: 18px;
          font-weight: 700;
          padding-bottom: 10px;
          border-bottom: 2px solid #e74c3c;
        }

        .popup-address {
          margin-bottom: 15px;
        }

        .popup-address strong,
        .popup-coordinates strong {
          color: #2c3e50;
          font-size: 14px;
          font-weight: 600;
          display: block;
          margin-bottom: 8px;
        }

        .popup-body p {
          margin: 0 0 15px 0;
          color: #555;
          font-size: 14px;
          line-height: 1.5;
          background: #fff;
          padding: 10px;
          border-radius: 8px;
          border-left: 3px solid #3498db;
        }

        .popup-coordinates {
          padding: 15px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 10px;
          margin: 15px 0;
          color: white;
        }

        .coordinate-grid {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .coordinate-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: rgba(255, 255, 255, 0.1);
          padding: 8px 12px;
          border-radius: 6px;
          backdrop-filter: blur(10px);
        }

        .coordinate-label {
          font-size: 12px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }

        .coordinate-value {
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          font-family: "Courier New", monospace;
        }

        .popup-actions {
          margin-top: 15px;
          text-align: center;
        }

        .view-details-btn {
          background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 25px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(231, 76, 60, 0.3);
        }

        .view-details-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(231, 76, 60, 0.4);
          background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
        }

        .mapboxgl-popup-close-button {
          font-size: 20px;
          color: #666;
          right: 8px;
          top: 8px;
        }

        .mapboxgl-popup-close-button:hover {
          color: #ff5722;
        }

        .mapboxgl-ctrl-group {
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .mapboxgl-ctrl button {
          border-radius: 6px;
        }

        .mapboxgl-ctrl button:hover {
          background: #667eea;
        }
      `}</style>
    </div>
  );
};

// Backward compatibility function
export const initializeMap = (containerId, locations = []) => {
  try {
    const container =
      typeof containerId === "string"
        ? document.getElementById(containerId)
        : containerId;

    if (!container) {
      console.error("Map container not found:", containerId);
      return null;
    }

    if (!mapboxgl.accessToken || mapboxgl.accessToken.includes("demo")) {
      console.error("Please set REACT_APP_MAPBOX_TOKEN environment variable");
      return null;
    }

    const map = new mapboxgl.Map({
      container: container,
      style: "mapbox://styles/mapbox/light-v11",
      center: [-123.1207, 49.2827],
      zoom: 10,
      pitch: 30,
      antialias: true,
    });

    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    locations.forEach((location, index) => {
      if (location.lat && location.lng) {
        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
            <div style="padding: 10px;">
              <h3 style="margin: 0 0 5px 0;">${location.name || "Location"}</h3>
              <p style="margin: 0; color: #666; font-size: 14px;">📍 ${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}</p>
            </div>
          `);

        new mapboxgl.Marker({
          color: "#FF5722",
          scale: 0.8,
        })
          .setLngLat([location.lng, location.lat])
          .setPopup(popup)
          .addTo(map);
      }
    });

    return map;
  } catch (error) {
    console.error("Error initializing map:", error);
    return null;
  }
};

export default MapComponent;
